import { FreshSurveyDebugType } from 'freemium-survey-components';
import moment from 'moment-timezone';
import {
  ACTIVE_SURVEY_LIMIT,
  CONTACT_LIMIT,
  CONTACT_LISTS_LIMIT,
  SEGMENTS_LIMIT,
  SURVEY_BLOCKS_LIMIT,
  SURVEY_COLLECTOR_LIMIT,
  SURVEY_LIMIT,
  USER_LIMIT,
  WORKFLOW_LIMIT,
} from 'routes/constants';
import {
  ConditionsType,
  CustomFieldType,
  FieldTypeIds,
  FieldTypes,
  FilterOperationsType,
  LimitsType,
  OptionType,
  TimeZone,
} from './types';

export const HEX_COLOR_REGEX = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;

export const alphabetColors = {
  a: '#D32F2F',
  b: '#D81B60',
  c: '#AB47BC',
  d: '#7E57C2',
  e: '#5C6BC0',
  f: '#1976D2',
  g: '#0277BD',
  h: '#006064',
  i: '#00796B',
  j: '#2E7D32',
  k: '#33691E',
  l: '#827717',
  m: '#F9A825',
  n: '#FF8F00',
  o: '#EF6C00',
  p: '#BF360C',
  q: '#8D6E63',
  r: '#757575',
  s: '#546E7A',
  t: '#00B8D4',
  u: '#00BFA5',
  v: '#0091EA',
  w: '#FBC02D',
  x: '#FF6D00',
  y: '#FF6E40',
  z: '#A1887F',
};

export const DEFAULT_TIME_FORMAT = '12';

const swap = (jsonObject) => {
  const ret = {};
  for (const key in jsonObject) {
    ret[jsonObject[key]] = key;
  }
  return ret;
};

// Unsupported Fields for now - 9, 11, 12, 14, 15, 16, 19, 21, 22
export const UNSUPPORTED_FIELDS_FOR_NOW: number[] = [
  9, 11, 12, 14, 15, 16, 19, 21, 22,
];
export const CONTACT_UPDATE_SUPPORTED_FIELD_TYPES = [1, 2, 6, 8, 5];
export const CONTACT_UPDATE_SUPPORTED_FIELD_NAMES: string[] = [
  'contact_status',
  'is_unsubscribed',
  'list_ids',
];
export const CONTACT_UPDATE_UNSUPPORTED_FIELD_NAMES: string[] = [
  'first_name',
  'last_name',
  'work_email',
  'work_number',
  'mobile_number',
];

export const CONTACT_CUSTOM_FIELD_NUMBER_LIMIT = 1999999999;

export const YUP_STRING_FIELD_TYPES: number[] = [1, 2, 3, 4, 6, 10, 20]; // Add 12, 16
export const YUP_NUMBER_FIELD_TYPES: number[] = [8, 13];
export const YUP_ARRAY_FIELD_TYPES: number[] = [18]; // Add 9, 22
export const YUP_BOOLEAN_FIELD_TYPES: number[] = [5];
export const YUP_DATE_FIELD_TYPES: number[] = [17];
export const YUP_DATE_TIME_FIELD_TYPES: number[] = [7]; // Add 19
export const YUP_OBJECT_FIELD_TYPES: number[] = []; // Add 15
export const YUP_MIXED_FIELD_TYPES: number[] = []; // Add 11, 14, 21

export const FREEMIUM_INPUT_DATE_TYPES: number[] = [17];
export const FREEMIUM_INPUT_DATE_TIME_TYPES: number[] = [7]; // Add 19
export const FREEMIUM_INPUT_NUMBER_TYPES: number[] = [8, 13, 20];
export const FREEMIUM_INPUT_TYPES: number[] = [1, 3, 4, 10];
export const FREEMIUM_TEXTAREA_TYPES: number[] = [6];
export const FREEMIUM_DROPDOWN_TYPES: number[] = [2]; // Add 16
export const FREEMIUM_CHECKBOX_TYPES: number[] = [5];
export const FREEMIUM_RADIO_TYPES: number[] = []; // Add 12
export const FREEMIUM_DEPENDENT_DROPDOWN_TYPES: number[] = []; // Add 15
export const FREEMIUM_MULTISELECT_DROPDOWN_TYPES: number[] = [18];

// Second element in a filter if the value can be given in a plain text box
export const FILTER_INPUT_VALUE_FIELDS: number[] = [1, 8];
export const FILTER_DATE_TIME_VALUE_FIELDS: number[] = [7];
export const FILTER_DATE_VALUE_FIELDS: number[] = [17];
export const FILTER_MULTIPLE_VALUE_FIELDS: number[] = [18];

export const COLUMNS_HIDE_ORDERING_KEY = 'name';

export const GLOBAL_DATE_TIME_FORMAT_12_HRS = 'YYYY-MMM-DD hh:mm A';
export const GLOBAL_DATE_FORMAT = 'YYYY-MM-DD';
export const GLOBAL_DATE_TIME_FORMAT_24_HRS = 'YYYY-MM-DD HH:mm';
export const MCR_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ssZ';
export const MCR_DATE_FORMAT = 'MM/DD/YYYY';
export const LOCAL_INPUT_DATE_TIME_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSS';
export const LOCAL_INPUT_DATE_FORMAT = 'YYYY-MM-DD';
export const LOCAL_INPUT_TIME_FORMAT = 'HH:mm';
export const GLOBAL_SURVEY_LIMIT_RESET_TIME_FORMAT_12 = 'MMM DD, YYYY hh:mm A';
export const GLOBAL_SURVEY_LIMIT_RESET_TIME_FORMAT_24 = 'MMM DD, YYYY HH:mm';

export const HIDE_CONTACT_DETAILS_FIELDS: string[] = ['list_ids'];
export const SHOW_CONTACT_DETAILS_FIELDS: string[] = ['contact_status'];
export const HIDE_UPDATE_FIELDS: string[] = [];
export const HIDE_CONTACT_FORM_FIELDS: string[] = [];
export const HIDE_IMPORT_CONTACT_FIELDS: string[] = [
  'list_ids',
  'created_at',
  'updated_at',
  'creater_id',
  'updater_id',
];
export const HIDE_EDIT_COLUUMNS_FIELDS: string[] = [];
export const HIDE_EXPORT_COLUUMNS_FIELDS: string[] = [
  'list_ids',
  'creater_id',
  'updater_id',
];
export const HIDE_SETTINGS_CUSTOM_FIELDS: string[] = ['list_ids'];
export const HIDE_EDIT_CUSTOM_FIELDS: string[] = [];
export const HIDE_DELETE_CUSTOM_FIELDS: string[] = ['language'];

export const BACK_END_FIELDS: string[] = [
  'last_emailed',
  'unsubscribe_time',
  'email_state',
];
export const TEMPORARY_HIDDEN_FIELDS: string[] = [
  'is_blocked',
  'status',
  'sub_state',
  'last_emailed',
];

// https://confluence.freshworks.com/display/PLAT/Fieldtype

export const ALL_FIELDS: Record<FieldTypeIds, FieldTypes> = {
  1: 'text',
  2: 'dropdown',
  3: 'email',
  4: 'phone_number',
  5: 'checkbox',
  6: 'paragraph',
  7: 'date_time',
  8: 'number',
  9: 'url_set',
  10: 'url',
  11: 'attachment',
  12: 'radio',
  13: 'decimal',
  14: 'section',
  15: 'dependent_dropdown',
  16: 'auto_complete',
  17: 'date',
  18: 'multi_select_dropdown',
  19: 'date_time_split',
  20: 'big_number',
  21: 'group_field',
  22: 'multi_select_checkbox',
};

export const ALL_FIELDS_BY_NAME = swap(ALL_FIELDS);

export const FilterOperations: FilterOperationsType = {
  // We will use IS_NOT_NULL instead of IS_NOT_EMPTY to also check for null conditions
  IS_NOT_NULL: { value: 'filter_operations.is_not_empty', id: 'IS_NOT_NULL' },
  // We will use IS_NULL instead of IS_EMPTY to also check for null conditions
  IS_NULL: { value: 'filter_operations.is_empty', id: 'IS_NULL' },
  IS: {
    value: 'filter_operations.is',
    id: 'IS',
    inputValueType: 1,
    needValue: true,
  },
  IS_EMPTY: { value: 'filter_operations.is_empty', id: 'IS_EMPTY' },
  IS_NOT_EMPTY: { value: 'filter_operations.is_not_empty', id: 'IS_NOT_EMPTY' },
  BETWEEN: {
    value: 'filter_operations.between',
    id: 'BETWEEN',
    between: true,
    needValue: true,
  },
  IS_NOT: {
    value: 'filter_operations.is_not',
    id: 'IS_NOT',
    inputValueType: 1,
    needValue: true,
  },
  AFTER: {
    value: 'filter_operations.after',
    id: 'AFTER',
    inputValueType: 7,
    needValue: true,
  },
  BEFORE: {
    value: 'filter_operations.before',
    id: 'BEFORE',
    inputValueType: 7,
    needValue: true,
  },
  BEFORE_NTH_DAY: {
    value: 'filter_operations.before_n_days',
    id: 'BEFORE_NTH_DAY',
    inputValueType: 8,
    needValue: true,
  },
  BEFORE_NTH_MINS: {
    value: 'filter_operations.before_n_minutes',
    id: 'BEFORE_NTH_MINS',
    inputValueType: 8,
    needValue: true,
  },
  CONTAINS: {
    value: 'filter_operations.contains',
    id: 'CONTAINS',
    inputValueType: 1,
    needValue: true,
  },
  DOES_NOT_CONTAINS: {
    value: 'filter_operations.does_not_contain',
    id: 'DOES_NOT_CONTAINS',
    inputValueType: 1,
    needValue: true,
  },
  ENDS_WITH: {
    value: 'filter_operations.ends_with',
    id: 'ENDS_WITH',
    inputValueType: 1,
    needValue: true,
  },
  GREATER_THAN: {
    value: 'filter_operations.greater_than',
    id: 'GREATER_THAN',
    inputValueType: 8,
    needValue: true,
  },
  GREATER_THAN_OR_EQUALS: {
    value: 'filter_operations.greater_than_or_equals',
    id: 'GREATER_THAN_OR_EQUALS',
    inputValueType: 8,
    needValue: true,
  },
  IN: {
    value: 'filter_operations.is',
    id: 'IN',
    inputValueType: 18,
    needValue: true,
  },
  LAST_MONTH: { value: 'filter_operations.last_month', id: 'LAST_MONTH' },
  LAST_N_DAYS: {
    value: 'filter_operations.last_n_days',
    id: 'LAST_N_DAYS',
    inputValueType: 8,
    needValue: true,
  },
  LAST_N_DAYS_INCLUDING_TODAY: {
    value: 'filter_operations.last_n_days_including_today',
    id: 'LAST_N_DAYS_INCLUDING_TODAY',
    inputValueType: 8,
    needValue: true,
  },
  LAST_NTH_MINS: {
    value: 'filter_operations.last_n_minutes',
    id: 'LAST_NTH_MINS',
    inputValueType: 8,
    needValue: true,
  },
  LAST_WEEK: { value: 'filter_operations.last_week', id: 'LAST_WEEK' },
  LESS_THAN: {
    value: 'filter_operations.less_than',
    id: 'LESS_THAN',
    inputValueType: 8,
    needValue: true,
  },
  LESS_THAN_OR_EQUALS: {
    value: 'filter_operations.less_than_or_equals',
    id: 'LESS_THAN_OR_EQUALS',
    inputValueType: 8,
    needValue: true,
  },
  NEXT_N_DAYS: {
    value: 'filter_operations.next_n_days',
    id: 'NEXT_N_DAYS',
    inputValueType: 8,
    needValue: true,
  },
  NEXT_N_DAYS_INCLUDING_TODAY: {
    value: 'filter_operations.next_n_days_including_today',
    id: 'NEXT_N_DAYS_INCLUDING_TODAY',
    inputValueType: 8,
    needValue: true,
  },
  NOT_IN: {
    value: 'filter_operations.is_not',
    id: 'NOT_IN',
    inputValueType: 18,
    needValue: true,
  },
  ON: {
    value: 'filter_operations.on',
    id: 'ON',
    inputValueType: 17,
    needValue: true,
    // Field type will be 17 inspite of the field.type from formserv
    overrideFieldType: true,
  },
  ON_NTH_DAY: {
    value: 'filter_operations.on_nth_day',
    id: 'ON_NTH_DAY',
    inputValueType: 8,
    needValue: true,
  },
  ON_NTH_MINS: {
    value: 'filter_operations.on_nth_minutes',
    id: 'ON_NTH_MINS',
    inputValueType: 8,
    needValue: true,
  },
  STARTS_WITH: {
    value: 'filter_operations.starts_with',
    id: 'STARTS_WITH',
    inputValueType: 1,
    needValue: true,
  },
  THIS_MONTH: { value: 'filter_operations.this_month', id: 'THIS_MONTH' },
  THIS_WEEK: { value: 'filter_operations.this_week', id: 'THIS_WEEK' },
  TODAY: { value: 'filter_operations.today', id: 'TODAY' },
  TOMORROW: { value: 'filter_operations.tomorrow', id: 'TOMORROW' },
  YESTERDAY: { value: 'filter_operations.yesterday', id: 'YESTERDAY' },
  NOT_CHECKED: { value: 'filter_operations.not_true', id: 'NOT_CHECKED' },
  CHECKED: { value: 'filter_operations.is_true', id: 'CHECKED' },
  IS_EMPTY_OR_NULL: {
    value: 'filter_operations.is_empty_or_null',
    id: 'IS_EMPTY_OR_NULL',
  },
  IS_NOT_EMPTY_AND_NOT_NULL: {
    value: 'filter_operations.is_empty_and_not_null',
    id: 'IS_NOT_EMPTY_AND_NOT_NULL',
  },
};

// date
const setA = [
  FilterOperations.BEFORE,
  FilterOperations.AFTER,
  FilterOperations.BETWEEN,
  FilterOperations.ON,
  FilterOperations.IS_NULL,
  FilterOperations.IS_NOT_NULL,
  FilterOperations.TODAY,
  FilterOperations.YESTERDAY,
  FilterOperations.THIS_WEEK,
  FilterOperations.THIS_MONTH,
  FilterOperations.LAST_WEEK,
  FilterOperations.LAST_MONTH,
  FilterOperations.LAST_N_DAYS,
  FilterOperations.LAST_N_DAYS_INCLUDING_TODAY,
  FilterOperations.NEXT_N_DAYS,
  FilterOperations.NEXT_N_DAYS_INCLUDING_TODAY,
  FilterOperations.BEFORE_NTH_DAY,
  FilterOperations.ON_NTH_DAY,
];

// date_time
const setB = [
  FilterOperations.BEFORE_NTH_MINS,
  FilterOperations.LAST_NTH_MINS,
  FilterOperations.ON_NTH_MINS,
];

// decimal, number, big_number
const setC = [
  FilterOperations.GREATER_THAN,
  FilterOperations.LESS_THAN,
  FilterOperations.IS,
  FilterOperations.GREATER_THAN_OR_EQUALS,
  FilterOperations.LESS_THAN_OR_EQUALS,
  FilterOperations.IS_NOT,
  FilterOperations.BETWEEN,
  FilterOperations.IS_NULL,
  FilterOperations.IS_NOT_NULL,
];

// radio
const setD = [
  FilterOperations.IS,
  // FilterOperations.IS_EMPTY,
  // FilterOperations.IS_NOT_EMPTY,
  FilterOperations.IS_NULL,
  FilterOperations.IS_NOT_NULL,
];

// text, email, paragraph, url, phone_number
const setE = [
  FilterOperations.IS,
  FilterOperations.IS_NOT,
  // FilterOperations.IS_EMPTY,
  // FilterOperations.IS_NOT_EMPTY,
  FilterOperations.IS_NULL,
  FilterOperations.IS_NOT_NULL,
  FilterOperations.CONTAINS,
  FilterOperations.DOES_NOT_CONTAINS,
  FilterOperations.STARTS_WITH,
  FilterOperations.ENDS_WITH,
];

// dropdown, auto_complete, multi_select_checkbox, multi_select_dropdown, group_field
const setF = [
  FilterOperations.IN,
  FilterOperations.NOT_IN,
  FilterOperations.IS_NULL,
  FilterOperations.IS_NOT_NULL,
];

// checkbox
const setG = [
  FilterOperations.CHECKED,
  FilterOperations.NOT_CHECKED,
  FilterOperations.IS_NULL,
  FilterOperations.IS_NOT_NULL,
];

// updater_id, creater_id
const setH = [
  FilterOperations.IS,
  FilterOperations.IS_NOT,
  FilterOperations.IS_NULL,
  FilterOperations.IS_NOT_NULL,
];

// https://confluence.freshworks.com/pages/viewpage.action?spaceKey=MCR&title=Bulk+Segmentation+Implementation

export const FieldBasedFilterOperations = {
  updater_id: setH,
  creater_id: setH,
  7: [...setA, ...setB],
  17: setA,
  8: setC,
  13: setC,
  20: setC,
  12: setD,
  1: setE,
  3: setE,
  6: setE,
  10: setE,
  4: setE,
  2: setF,
  16: setF,
  22: setF,
  18: setF,
  21: setF,
  5: setG,
};

export const timeZones: TimeZone[] = moment.tz
  .names()
  .map((tzName) => ({
    offset: moment.tz(tzName).format('Z'),
    timeZone: tzName,
  }))
  .filter((tz) => tz?.timeZone !== 'Europe/Kiev');

export const SEARCHABLE_FIELDS_IN_NEW_CONTACT_FORM = ['timezone', 'language'];

export const limits: LimitsType = {
  surveys: SURVEY_LIMIT,
  active_surveys: ACTIVE_SURVEY_LIMIT,
  workflows: WORKFLOW_LIMIT,
  users: USER_LIMIT,
  survey_collectors: SURVEY_COLLECTOR_LIMIT,
  contacts: CONTACT_LIMIT,
  contact_lists: CONTACT_LISTS_LIMIT,
  segments: SEGMENTS_LIMIT,
  survey_blocks: SURVEY_BLOCKS_LIMIT,
};

export const MFE_WC_EXPORTS = {
  name: 'surveyserv',
  modules: {
    SurveyTemplateWC: './SurveyTemplateWC',
    SurveyListWC: './SurveyListWC',
    SurveyBuilderWC: './SurveyBuilderWC',
    ThemeListWC: './SurveyThemeWC',
  },
};

export const fieldTypes: Array<CustomFieldType> = [
  {
    id: 'textbox',
    glyph: 'textbox',
    name: 'settings.custom_fields.field_types.textbox',
    type: 1,
    width: '29',
    height: '29',
    limit: 100,
  },
  {
    id: 'dropdown',
    glyph: 'dropdown',
    name: 'settings.custom_fields.field_types.dropdown',
    type: 2,
    width: '29',
    height: '29',
    limit: 35,
  },
  {
    id: 'paragraph',
    glyph: 'paragraph',
    name: 'settings.custom_fields.field_types.paragraph',
    type: 6,
    width: '29',
    height: '29',
    limit: 20,
  },
  {
    id: 'number',
    glyph: 'number',
    name: 'settings.custom_fields.field_types.number',
    type: 8,
    width: '29',
    height: '29',
    limit: 50,
  },
  {
    id: 'datePicker',
    glyph: 'date-picker',
    name: 'settings.custom_fields.field_types.datePicker',
    type: 7,
    width: '14',
    height: '14',
    limit: 20,
  },
  {
    id: 'checkbox',
    glyph: 'checkbox',
    name: 'settings.custom_fields.field_types.checkbox',
    type: 5,
    width: '14',
    height: '14',
    limit: 25,
  },
  {
    id: 'phoneNumber',
    glyph: 'phone-number',
    name: 'settings.custom_fields.field_types.phoneNumber',
    type: 4,
    width: '29',
    height: '29',
    limit: 10,
    metaData: {
      regex: {
        message: 'Should be in E.164 format',
        validation: '^\\+[1-9]\\d{5,14}$',
      },
    },
  },
];

export const LANGUAGE_OPTIONS: OptionType[] = [
  { label: 'Arabic', id: 'ar' },
  { label: 'Dutch', id: 'nl' },
  { label: 'English', id: 'en' },
  { label: 'French', id: 'fr' },
  { label: 'German', id: 'de' },
  { label: 'Hebrew', id: 'he' },
  { label: 'Portuguese (BR)', id: 'pt-br' },
  { label: 'Spanish', id: 'es' },
  { label: 'Spanish (Latin America)', id: 'es-la' },
  { label: 'Swedish', id: 'sv' },
];

export const CHANNELS: ConditionsType[] = [
  {
    id: 'EMAIL',
    name: 'channel.email',
    value: 'EMAIL',
  },
  {
    id: 'WEB_APP',
    name: 'channel.web_app',
    value: 'WEB_APP',
  },
  {
    id: 'EMAIL_EMBED',
    name: 'channel.email_embed',
    value: 'EMAIL_EMBED',
  },
  {
    id: 'WEB_LINK',
    name: 'channel.shareable_link',
    value: 'WEB_LINK',
  },
];

export const FRESH_SURVEY_DEBUG: keyof FreshSurveyDebugType = 'fwa:debug';
